// =============================================================================
// stripMarketsToDisplay — convert VideoOddsStripMarket[] → DisplayMarket[]
// =============================================================================
// `VideoOddsStripMarket` is the engine-shipped shape for the persistent odds
// strip (used by Stories). The same set of markets is shown by the standalone
// `<PopularOddsWidget>` under article manchets. The site widget reuses the
// existing render-layer particles (`<OddsCarousel>` / `<OddsMarket>` /
// `<OddsButton>`) which speak the `DisplayMarket` shape, so we adapt here.
//
// `snapshotPrices` are stored as provider milliodds (× 1000); divide back to
// decimal odds for the button. Outcomes preserve their `outcomeIds` so a
// future betslip integration can wire them through unchanged.
// =============================================================================

import type {
  VideoOddsStripMarket,
  DisplayMarket,
  OddsOutcome,
  OutcomeType,
} from '@aamp/shared-types';

function priceToDecimal(price: number | undefined): number | null {
  if (price === undefined || !Number.isFinite(price)) return null;
  return price / 1000;
}

/**
 * The catalogue's own outcome words, in the tenant's locale.
 *
 * WHY THIS EXISTS: the English literals in the switch below used to be the
 * only fallback, and the engine bakes `yes` / `no` into a gem's odds strip
 * untranslated — so a Danish board on /news read "Yes / No" beside Danish
 * prices (live defect, 2026-09-08). The catalogue has had `odds.yes`,
 * `odds.no`, `odds.over` and `odds.under` in da-DK, en-GB and sv-SE all
 * along; nothing was passing them in.
 *
 * PRECEDENCE, and it differs per outcome on purpose:
 *   - `two-way-yesno` — the CATALOGUE wins. Yes/No is the template's own
 *     vocabulary; it states no fact, so localising it invents nothing.
 *   - everywhere else — the ENGINE wins, and these are only a fallback. A
 *     team name and "Over 2.5" carry facts, and the engine owns facts
 *     (CLAUDE.md rule 5).
 *
 * Callers inside React hand this the result of `useAdapterStrings()`; the
 * module stays pure (no React, no i18n) so it is still testable and usable
 * off the render path. Omitting it keeps the English defaults — a non-React
 * caller still gets readable output rather than a blank button.
 */
export interface StripOutcomeLabels {
  yes?: string;
  no?: string;
  /** Called with the market's line ("2.5"), because Danish says "Over 2,5". */
  over?: (line: string) => string;
  under?: (line: string) => string;
  homeFallback?: string;
  awayFallback?: string;
}

interface StripMarketsToDisplayOptions {
  /** Maps three-way template positions to OutcomeType (default: 1/X/2). */
  threeWayTypes?: [OutcomeType, OutcomeType, OutcomeType];
  /** Localised fallbacks for outcomes the strip did not label. */
  labels?: StripOutcomeLabels;
}

/**
 * Convert a single `VideoOddsStripMarket` into a `DisplayMarket` the
 * existing `<OddsMarket>` particle can render. Pure — no React, no i18n
 * (labels come from the engine via the strip, the adapter for text
 * articles supplies them via i18next).
 */
export function stripMarketToDisplay(
  m: VideoOddsStripMarket,
  opts: StripMarketsToDisplayOptions = {},
): DisplayMarket {
  const { threeWayTypes = ['OT_ONE', 'OT_CROSS', 'OT_TWO'], labels: words } = opts;
  const labels = m.oddsRef.labels ?? {};
  const prices = m.snapshotPrices.prices;
  // The strip carries no line of its own, so a fallback Over/Under label is
  // the bare word: `odds.over` is "Over {{line}}", and an empty line leaves
  // "Over". A number is never invented here.
  const word = (fn: ((line: string) => string) | undefined): string | undefined =>
    fn ? fn('').trim() : undefined;

  // Resolve per-template ordered (label, type) pairs so the buttons render
  // in expected slots (1/X/2, Over/Under, Yes/No, Home/Away).
  let ordered: Array<{ label: string; type: OutcomeType }>;
  switch (m.template) {
    case 'three-way':
      ordered = [
        { label: labels['1'] ?? '1', type: threeWayTypes[0] },
        { label: labels['X'] ?? 'X', type: threeWayTypes[1] },
        { label: labels['2'] ?? '2', type: threeWayTypes[2] },
      ];
      break;
    case 'two-way-total':
      ordered = [
        { label: labels['over'] ?? word(words?.over) ?? 'Over', type: 'OT_OVER' },
        { label: labels['under'] ?? word(words?.under) ?? 'Under', type: 'OT_UNDER' },
      ];
      break;
    case 'two-way-yesno':
      // The CATALOGUE WINS here, unlike every other branch. "Yes" and "No"
      // are the template's own vocabulary — they carry no fact, name no
      // team and state no line — and the engine bakes them into the strip
      // untranslated, so a Danish board read "Yes / No" beside Danish
      // prices on the live tenant (2026-09-08). Locale is applied at the
      // final boundary (ADR-047), and this is that boundary. Everywhere a
      // label carries a FACT (a team name, "Over 2.5") the engine still
      // wins — see the branches above and below.
      ordered = [
        { label: words?.yes ?? labels['yes'] ?? 'Yes', type: 'OT_YES' },
        { label: words?.no ?? labels['no'] ?? 'No', type: 'OT_NO' },
      ];
      break;
    case 'two-way-ah':
      ordered = [
        { label: labels['home'] ?? words?.homeFallback ?? 'Home', type: 'OT_UNTYPED' },
        { label: labels['away'] ?? words?.awayFallback ?? 'Away', type: 'OT_UNTYPED' },
      ];
      break;
    case 'two-way':
      // Generic 2-outcome (DNB, qualification, "to lift trophy", etc.).
      // Engine labels each outcome explicitly under `labels.a` / `labels.b`;
      // we render in order. OT_UNTYPED keeps the engine row free from
      // expectations about home/away/yes-no semantics.
      ordered = [
        { label: labels['a'] ?? labels['1'] ?? '1', type: 'OT_UNTYPED' },
        { label: labels['b'] ?? labels['2'] ?? '2', type: 'OT_UNTYPED' },
      ];
      break;
    default:
      ordered = m.oddsRef.outcomeIds.map((_, i) => ({
        label: Object.values(labels)[i] ?? String(i + 1),
        type: 'OT_UNTYPED',
      }));
  }

  const outcomes: OddsOutcome[] = m.oddsRef.outcomeIds.map((id, i) => {
    const meta = ordered[i];
    const decimal = priceToDecimal(prices[String(id)]);
    return {
      id,
      label: meta?.label ?? String(i + 1),
      odds: decimal,
      type: meta?.type ?? 'OT_UNTYPED',
      status: decimal === null ? 'SUSPENDED' : 'OPEN',
    };
  });

  return {
    key: m.id,
    title: m.label,
    layout: 'row',
    outcomes,
  };
}

/** Vector form of {@link stripMarketToDisplay}. */
export function stripMarketsToDisplay(
  markets: ReadonlyArray<VideoOddsStripMarket>,
  opts: StripMarketsToDisplayOptions = {},
): DisplayMarket[] {
  return markets.map((m) => stripMarketToDisplay(m, opts));
}
